import { z } from 'zod';
import {
  calendarDateSchema,
  centsSchema,
  nonEmptyString,
  optionalText,
  timeOfDaySchema,
  uuidSchema,
} from './common';

/** A line item on a job: a service with quantity and price. */
export const jobLineItemSchema = z.object({
  serviceId: uuidSchema.optional(),
  description: nonEmptyString,
  quantity: z
    .number({ message: 'Enter a quantity' })
    .nonnegative({ message: 'Quantity must not be negative' })
    .default(1),
  unitPriceCents: centsSchema,
});

export const createJobSchema = z.object({
  customerId: uuidSchema,
  propertyId: uuidSchema,
  title: nonEmptyString,
  scheduledDate: calendarDateSchema,
  scheduledStartTime: timeOfDaySchema.optional(),
  estimatedDurationMinutes: z
    .number({ message: 'Enter a duration in minutes' })
    .int({ message: 'Duration must be a whole number of minutes' })
    .nonnegative({ message: 'Duration must not be negative' })
    .default(60),
  services: z.array(jobLineItemSchema).min(1, { message: 'Add at least one service' }),
  assignedUserId: uuidSchema.optional().nullable(),
  /**
   * Additional assignees beyond the primary. On PATCH: absent key = unchanged,
   * present array = replace-all, [] = clear. Every id must name an ACTIVE user
   * in the caller's organization, the same rule as the primary assignee.
   */
  additionalAssigneeIds: z.array(uuidSchema).optional(),
});

export const updateJobSchema = createJobSchema
  .partial()
  .refine((data) => Object.keys(data).length > 0, { message: 'Nothing to update' });

export const completeJobSchema = z.object({
  completionNotes: optionalText,
  actualCompletedAt: z.string().optional(),
});

export const rescheduleJobSchema = z.object({
  scheduledDate: calendarDateSchema,
  scheduledStartTime: timeOfDaySchema.optional(),
});

export type CreateJobInput = z.infer<typeof createJobSchema>;
export type UpdateJobInput = z.infer<typeof updateJobSchema>;
export type CompleteJobInput = z.infer<typeof completeJobSchema>;
export type RescheduleJobInput = z.infer<typeof rescheduleJobSchema>;
